import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import { useShortcutManagersStore } from 'app/services/pinia/shortcut-managers-store'
import { useServices } from 'app/fragments/use-services'
import { $T } from 'client/library/translation'
import type { ToolBarItem, TopicModifyToolbarItemKey } from 'client/pages/app/fragments/shared/editor-top-bar/types'
import clsx from 'clsx'
import { usePresetConfig } from 'client/pages/integration-embed-editor/composables/use-preset-config'
import { useWindowResize } from 'client/composables/window-resize'
import { useEditorFeaturePaywall } from 'app/composables/editor-feature-paywall'
import { trackNewVersionWorks } from 'client/library/tracker'

export const useEmbedToolBarItems = () => {
  const { workbook } = storeToRefs(useWorkspaceStore())
  const services = useServices()
  const { windowWidth } = useWindowResize()

  const presetConfig = usePresetConfig()
  const currentScene = presetConfig.currentScene
  const { isEditorCommonPaidFeatureEnabled, showEditorFeatureLimitPaywall } = useEditorFeaturePaywall()

  const selection = computed(() => workbook.value?.workbookEditor?.selection.value)

  const isSingleTopicSelected = computed(
    () => selection.value?.type === 'topic' && !selection.value?.isMulti && selection.value?.modelIds?.length === 1,
  )
  const isTopicSelected = computed(() => selection.value?.type === 'topic' && (selection.value?.modelIds?.length ?? 0) > 0)

  const isSingleImageSelect = computed(
    () => selection.value?.type === 'image' && !selection.value?.isMulti && selection.value?.modelIds?.length === 1,
  )

  const document = computed(() => workbook.value?.workbookEditor?.document.value)
  const isSingleEquationSelect = computed(() => {
    if (!selection.value || selection.value.isMulti || selection.value.modelIds?.length !== 1) return false
    const topic = document.value?.query({ id: selection.value.modelIds[0] })?.asTopic()
    return !!topic?.extensionBy?.('Equation')
  })

  const getShortcutManager = () => useShortcutManagersStore().getShortcutManager('sharing-editor')

  const topicInsertToolbarItems = computed<ToolBarItem[]>(() => {
    const shortcutManager = getShortcutManager()

    return [
      {
        key: 'TOPIC',
        name: $T('Topic'),
        tooltip: $T('Add a topic after the selected topic.'),
        shortcut: shortcutManager?.findDescriptionByAction(workbook.value?.getAction('editor.addTopic')),
        icon: 'add-topic',
        enable: Boolean(workbook.value?.getAction('editor.addTopic')?.isEnabled),
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Topic')
          workbook.value?.getAction('editor.addTopic')?.execute()
        },
      },
      {
        key: 'SUBTOPIC',
        name: $T('Subtopic'),
        tooltip: $T('Add a child topic to the selected topic.'),
        shortcut: shortcutManager?.findDescriptionByAction(workbook.value?.getAction('editor.addSubTopic')),
        icon: 'add-subtopic',
        enable: Boolean(workbook.value?.getAction('editor.addSubTopic')?.isEnabled),
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Subtopic')
          workbook.value?.getAction('editor.addSubTopic')?.execute()
        },
      },
    ]
  })

  const topicModifyToolbarItems = computed<ToolBarItem[]>(() => {
    const addRelationshipAction = workbook.value?.getAction('editor.addRelationship')
    const addBoundaryAction = workbook.value?.getAction('editor.addBoundary')
    const addSummaryAction = workbook.value?.getAction('editor.addSummary')
    const shortcutManager = getShortcutManager()

    return [
      {
        key: 'RELATIONSHIP',
        name: $T('Relationship'),
        tooltip: $T('Create a relationship between the two topics.'),
        shortcut: shortcutManager?.findDescriptionByAction(addRelationshipAction),
        icon: 'relationship',
        enable: Boolean(addRelationshipAction?.isEnabled),
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Relationship')
          addRelationshipAction?.execute()
        },
      },
      {
        key: 'BOUNDARY',
        name: $T('Boundary'),
        tooltip: $T('Group the selected topics with boundary.'),
        shortcut: 'CmdOrCtrl + Shift + B',
        icon: 'boundary',
        enable: Boolean(addBoundaryAction?.isEnabled),
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Boundary')
          addBoundaryAction?.execute()
        },
      },
      {
        key: 'SUMMARY',
        name: $T('Summary'),
        tooltip: $T('Add a summary to the selected topics.'),
        icon: 'summary',
        enable: Boolean(addSummaryAction?.isEnabled),
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Summary')
          addSummaryAction?.execute()
        },
      },
    ]
  })

  const notSubscribedClass = clsx('rounded bg-[#FA4A1E26] text-xred-500')
  const topicInsertItemToolbarItems = computed<ToolBarItem[]>(() => {
    const noteAction = services?.action?.ui?.openNotePopover
    const labelAction = services?.action?.ui?.openLabelPopover
    const hyperlinkAction = services?.action?.ui?.openHyperlinkPopover
    const importImageAction = services?.action?.dialog?.openImportImageDialog
    const addTodoAction = workbook.value?.getAction('editor.addTodo')
    const shortcutManager = getShortcutManager()
    const isSubscribed = isEditorCommonPaidFeatureEnabled.value

    const items: ToolBarItem[] = [
      {
        key: 'NOTE',
        name: $T('Note'),
        tooltip: $T('Add a note to the selected topic.'),
        shortcut: shortcutManager?.findDescriptionByAction(noteAction),
        icon: 'note',
        enable: isSingleTopicSelected.value,
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Note')
          noteAction?.execute?.({ status: true })
        },
      },
      {
        key: 'TASK',
        name: $T('To-Do'),
        tooltip: $T('Add a task to the selected topics.'),
        shortcut: shortcutManager?.findDescriptionByAction(addTodoAction),
        icon: 'task',
        iconClass: isSubscribed ? '' : notSubscribedClass,
        enable: Boolean(addTodoAction?.isEnabled),
        action: () => {
          trackNewVersionWorks.taskToolbar()
          showEditorFeatureLimitPaywall()
        },
      },
      {
        key: 'PLANNED_TASK',
        name: $T('Task'),
        tooltip: $T('Add a planned task to the selected topic.'),
        icon: 'planned',
        enable: isSingleTopicSelected.value,
        iconClass: isSubscribed ? '' : notSubscribedClass,
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Planned Task')
          showEditorFeatureLimitPaywall()
        },
      },
      {
        key: 'HYPERLINK',
        name: $T('Hyperlink'),
        tooltip: $T('Add a hyperlink to the selected topic.'),
        shortcut: shortcutManager?.findDescriptionByAction(hyperlinkAction),
        icon: 'note-link',
        enable: Boolean(hyperlinkAction?.isEnabled),
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Hyperlink')
          hyperlinkAction?.execute?.({ status: true })
        },
      },
      {
        key: 'LABEL',
        name: $T('Label'),
        tooltip: $T('Add labels to the selected topic.'),
        shortcut: shortcutManager?.findDescriptionByAction(labelAction),
        icon: 'lable',
        enable: isTopicSelected.value,
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Label')
          labelAction?.execute?.({ status: true })
        },
      },
      {
        key: 'IMAGE',
        name: $T('Image'),
        tooltip: $T('Add Image to the selected topic.'),
        shortcut: shortcutManager?.findDescriptionByAction(importImageAction),
        icon: 'photo',
        enable: isSingleTopicSelected.value || isSingleImageSelect.value,
        action: async () => {
          trackNewVersionWorks.editorToolbarClick('Image')
          await importImageAction?.()
        },
      },
      {
        key: 'EQUATION',
        name: $T('Equation'),
        tooltip: $T('Add equation to the selected topic.'),
        icon: 'equation',
        iconClass: isSubscribed ? '' : notSubscribedClass,
        enable: isSingleTopicSelected.value || isSingleEquationSelect.value,
        action: () => {
          trackNewVersionWorks.editorToolbarClick('Equation')
          showEditorFeatureLimitPaywall()
        },
      },
    ]

    if (currentScene === 'default') {
      return items.filter(item => item.iconClass !== notSubscribedClass)
    }
    return items
  })

  const topicModifyToolbarItemsResponsiveRule = computed<TopicModifyToolbarItemKey[]>(() => {
    if (windowWidth.value < 900) {
      return []
    }

    if (windowWidth.value < 960) {
      return ['RELATIONSHIP']
    }

    if (windowWidth.value < 1020) {
      return ['RELATIONSHIP', 'BOUNDARY']
    }

    return ['RELATIONSHIP', 'BOUNDARY', 'SUMMARY']
  })

  return {
    topicInsertToolbarItems,
    topicModifyToolbarItems,
    topicInsertItemToolbarItems,
    topicModifyToolbarItemsResponsiveRule,
  }
}
